import React, { Component } from "react";
import { Translate } from "react-redux-i18n";
import { getMenuItem, getPriceForQuantity } from "../../js/admin";
import auth, { _log } from "../../js/auth";
import { getProxyPercentWidth } from "../../js/auth";
import "./UpsellingSuggestions.css";

/**
 * UpsellingSuggestions Component
 * Displays AI-recommended products at checkout
 * Allows users to add suggestions to their cart or skip
 */
class UpsellingSuggestions extends Component {
  state = {
    selectedSuggestions: new Set(), // Track which suggestions user wants to add
    isAdding: false,
    groupedSuggestionIndex: {} // key -> active variant index within a grouped box
  };

  groupRefs = {};

  componentDidMount() {
    this.measureGroupHeights();
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevProps.suggestions !== this.props.suggestions ||
      prevState.groupedSuggestionIndex !== this.state.groupedSuggestionIndex ||
      prevProps.show !== this.props.show
    ) {
      this.measureGroupHeights();
    }
  }

  measureGroupHeights = () => {
    Object.keys(this.groupRefs).forEach((groupKey) => {
      const container = this.groupRefs[groupKey];
      if (!container) return;

      const panels = container.querySelectorAll(".upselling-option-panel");
      let maxHeight = 0;

      panels.forEach((panel) => {
        const previousPosition = panel.style.position;
        const previousVisibility = panel.style.visibility;
        const previousOpacity = panel.style.opacity;
        const previousPointerEvents = panel.style.pointerEvents;

        panel.style.position = "static";
        panel.style.visibility = "hidden";
        panel.style.opacity = "0";
        panel.style.pointerEvents = "none";

        maxHeight = Math.max(maxHeight, panel.scrollHeight || 0);

        panel.style.position = previousPosition;
        panel.style.visibility = previousVisibility;
        panel.style.opacity = previousOpacity;
        panel.style.pointerEvents = previousPointerEvents;
      });

      if (maxHeight > 0) {
        container.style.height = `${maxHeight}px`;
      }
    });
  };

  getSuggestionGroupKey = (suggestion) => {
    const categoryKey = suggestion?.categoryId
      || suggestion?.category?.id
      || suggestion?.menuCategoryId
      || suggestion?.parentCategoryId
      || suggestion?.category?.parentCategoryId
      || suggestion?.menuCategory?.id
      || suggestion?.menuCategory?.parentCategoryId;

    if (categoryKey != null && categoryKey !== "") {
      return categoryKey;
    }

    return suggestion?.entityType || suggestion?.id;
  };

  findCategoryInTree = (categories, categoryId) => {
    if (!Array.isArray(categories) || categoryId == null) return null;

    const normalizedId = String(categoryId);
    for (const category of categories) {
      if (!category) continue;

      if (String(category.id) === normalizedId) {
        return category;
      }

      const nestedCategories = category.activeMenuCategories || category.children || category.menuCategories || category.categories || [];
      const found = this.findCategoryInTree(nestedCategories, normalizedId);
      if (found) {
        return found;
      }
    }

    return null;
  };

  getCategoryItems = (category) => {
    if (!category) return [];

    const items = Array.isArray(category.activeMenuItems) ? category.activeMenuItems : [];
    const nestedCategories = category.activeMenuCategories || category.children || category.menuCategories || category.categories || [];
    const nestedItems = nestedCategories.flatMap((child) => this.getCategoryItems(child));

    return [...items, ...nestedItems];
  };

  getSuggestionPrice = (menuItem, fallback = 0) => {
    if (!menuItem) return Number(fallback) || 0;

    const hasQuantities = Array.isArray(menuItem.availableQuantities) && menuItem.availableQuantities.length > 0;
    const quantity = hasQuantities ? menuItem.availableQuantities[0]?.quantity || 1 : 1;

    try {
      if (hasQuantities) {
        const price = getPriceForQuantity(menuItem, quantity, false, false);
        if (!Number.isNaN(Number(price)) && Number(price) >= 0) {
          return Number(price);
        }
      }

      const directPrice = Number(menuItem.unitPrice ?? menuItem.price ?? menuItem.defaultPrice ?? fallback);
      if (!Number.isNaN(directPrice)) {
        return directPrice;
      }
    } catch (error) {
      console.warn("[UPSELL] Failed to calculate menu item price via kiosk logic", menuItem?.id, error);
    }

    return Number(fallback) || 0;
  };

    hasItemImage = (menuItem) => {
        if (!menuItem) return false;

        return Boolean(
            menuItem.bigImage ||
            menuItem.image
        );
    };

    hasSuggestionImage = (suggestion) => {
        if (!suggestion) return false;

        const menuItem = getMenuItem(suggestion.id);
        if (this.hasItemImage(menuItem)) {
            return true;
        }

        return false;
    };

    isSuggestionDeleted = (suggestion) => {
        if (!suggestion || suggestion.id == null) {
            return false;
        }

        const menuItem = getMenuItem(suggestion.id);
        return menuItem?.recordState === "DELETED";
    };

  groupSuggestions = (suggestions = []) => {
      const topSuggestions = (suggestions || [])
          .filter((suggestion) => !this.isSuggestionDeleted(suggestion) && this.hasSuggestionImage(suggestion))
          .map((suggestion) => {
            const menuItem = getMenuItem(suggestion.id) || suggestion;
            return {
              ...suggestion,
              price: this.getSuggestionPrice(menuItem, suggestion.price || 0)
            };
          })
          .slice(0, 4); // Limit to top 6 suggestions
    const groups = new Map();
    const baseOrder = [];
    const seenIds = new Set();
    const menuCategories = Array.isArray(this.props?.menuCategories) ? this.props.menuCategories : [];

    const addVariant = (suggestion, orderIndex) => {
      if (!suggestion || suggestion.id == null || seenIds.has(Number(suggestion.id))) {
        return;
      }

      const key = this.getSuggestionGroupKey(suggestion);
      if (!groups.has(key)) {
        groups.set(key, []);
        baseOrder.push(key);
      }

      groups.get(key).push({ ...suggestion, __order: orderIndex });
      seenIds.add(Number(suggestion.id));
    };

    topSuggestions.forEach((suggestion, index) => {
      addVariant(suggestion, index);
    });

    topSuggestions.forEach((suggestion, index) => {
      const categoryId = this.getSuggestionGroupKey(suggestion);
      const category = this.findCategoryInTree(menuCategories, categoryId);
        const categoryItems = this.getCategoryItems(category).filter(item => item.isTop && item.defaultKiosk && item.defaultAvailable);

        categoryItems
            .filter((menuItem) => this.hasItemImage(menuItem))
            .forEach((menuItem, menuIndex) => {
                if (!menuItem || menuItem.id == null) return;
                if (seenIds.has(Number(menuItem.id))) return;
                addVariant({
                    id: menuItem.id,
                    name: menuItem.name,
                    description: menuItem.description,
                    price: this.getSuggestionPrice(menuItem, suggestion.price || 0),
                    currency: suggestion.currency || menuItem.currency || "€",
                    relevanceScore: 0.35,
                    aiReason: suggestion.aiReason || "",
                    categoryId: category?.id || categoryId,
                    entityType: menuItem.entityType || suggestion.entityType || "Item"
                }, index + 1 + menuIndex);
            });
    });

    return baseOrder
      .map((key) => groups.get(key))
      .filter(Boolean)
      .map((group) => group.sort((a, b) => (a.__order || 0) - (b.__order || 0)))
      .sort((a, b) => (a[0].__order || 0) - (b[0].__order || 0));
  };

  getCurrentGroupIndex = (groupKey) => {
    const { groupedSuggestionIndex } = this.state;
    return groupedSuggestionIndex[groupKey] || 0;
  };

  setGroupIndex = (groupKey, nextIndex) => {
    const { groupedSuggestionIndex } = this.state;
    const current = this.getCurrentGroupIndex(groupKey);
    const next = Math.max(0, Math.min(nextIndex, current === undefined ? 0 : Number.MAX_SAFE_INTEGER));
    this.setState({
      groupedSuggestionIndex: {
        ...groupedSuggestionIndex,
        [groupKey]: next
      }
    });
  };

  changeVariant = (groupKey, direction) => {
    const groups = this.groupSuggestions(this.props.suggestions || []);
    const group = groups.find(g => this.getSuggestionGroupKey(g[0]) === groupKey) || [];
    if (!group.length) return;

    const currentIndex = this.getCurrentGroupIndex(groupKey);
    const nextIndex = (currentIndex + direction + group.length) % group.length;

    this.setState({
      groupedSuggestionIndex: {
        ...this.state.groupedSuggestionIndex,
        [groupKey]: nextIndex
      }
    });
  };

  handleToggleSuggestion = (suggestionId) => {
    const { selectedSuggestions } = this.state;
    const newSelected = new Set(selectedSuggestions);
    if (newSelected.has(suggestionId)) {
      newSelected.delete(suggestionId);
    } else {
      newSelected.add(suggestionId);
    }
    this.setState({ selectedSuggestions: newSelected });
  };

  handleAddSelected = () => {
    const { selectedSuggestions } = this.state;
    const { suggestions, onAddItems } = this.props;

    const itemsToAdd = suggestions.filter(s => selectedSuggestions.has(s.id));

    if (itemsToAdd.length > 0 && onAddItems) {
      this.setState({ isAdding: true });
      onAddItems(itemsToAdd, () => {
        // After adding items, return to ordering (upsellItemsAdded will be true in Kiosk state)
        this.setState({ isAdding: false, selectedSuggestions: new Set() });
        const { onAddItemsComplete } = this.props;
        if (onAddItemsComplete) {
          onAddItemsComplete();
        }
      });
    }
  };

  handleCancel = () => {
    const { onCancel } = this.props;
    if (onCancel) {
      onCancel();
    }
  };

  handleSkip = () => {
    // Check if items were added from upselling (passed as prop from Kiosk.js)
    const { upsellItemsAdded, onCancel, onClose } = this.props;
    
    if (upsellItemsAdded) {
      // Items were added, return to main screen without proceeding to payment
      if (onCancel) {
        onCancel();
      }
    } else {
      // No items were added, proceed to payment
      if (onClose) {
        onClose();
      }
    }
  };

  handleAcceptOne = (suggestion) => {
    const { onAddItems } = this.props;
    if (onAddItems) {
      this.setState({ isAdding: true });
      onAddItems([suggestion], () => {
        this.setState({ isAdding: false });
      });
    }
  };

  /**
   * Get image URL for a suggestion using the same logic as MenuItemButton
   * Uses bigImage if available, otherwise image, or empty string
   */
  getImageUrl = (suggestion) => {
    try {
      // Get full menu item from admin cache
      const menuItem = getMenuItem(suggestion.id);

        if (menuItem) {
            if (menuItem.bigImage || menuItem.bigImageSrc) {
                const bigImageName = menuItem.bigImage || menuItem.bigImageSrc;
                return getProxyPercentWidth(
                    60,
                    `${auth.server}/${auth.war}/tableService/${localStorage.restaurantSelected}/getImage/${bigImageName}?timestamp=${menuItem.bigImageTimestamp || ""}&instance=${localStorage.instance}`
                );
            }

            if (menuItem.image || menuItem.imageSrc) {
                const imageName = menuItem.image || menuItem.imageSrc;
                return getProxyPercentWidth(
                    60,
                    `${auth.server}/${auth.war}/tableService/${localStorage.restaurantSelected}/getImage/${imageName}?timestamp=${menuItem.imageTimestamp || ""}&instance=${localStorage.instance}`
                );
            }
        }

        if (suggestion.bigImage || suggestion.bigImageSrc) {
            return suggestion.bigImage || suggestion.bigImageSrc;
        }

        if (suggestion.image || suggestion.imageSrc) {
            return suggestion.image || suggestion.imageSrc;
        }

        if (suggestion.imageUrl || suggestion.bigImageUrl) {
            return suggestion.imageUrl || suggestion.bigImageUrl;
        }

        if (suggestion.picture || suggestion.pictureUrl) {
            return suggestion.picture || suggestion.pictureUrl;
        }

        return '';
    } catch (error) {
      console.error("[UPSELL] Error getting image URL:", error);
        return suggestion.imageUrl || suggestion.bigImageUrl || suggestion.pictureUrl || '';
    }
  };

  render() {
    const { suggestions, show, language } = this.props;
    const { selectedSuggestions, isAdding } = this.state;

    if (!show || !suggestions || suggestions.length === 0) {
      return null;
    }
      const groupedSuggestions = this.groupSuggestions(suggestions).slice(0, 4);
    return (
      <div className="upselling-overlay" onClick={() => !isAdding && this.handleCancel()}>
        <div className="upselling-modal" onClick={(e) => e.stopPropagation()}>
          <div className="upselling-header">
            <h2>
              <Translate value="local.upsellingSuggestions" />
            </h2>
            <button
              className="upselling-close-btn"
              onClick={this.handleCancel}
              disabled={isAdding}
            >
              ✕
            </button>
          </div>

          <div className="upselling-body">
            <p className="upselling-subtitle">
              <Translate value="local.upsellingSuggestionsDescription" />
            </p>

            <div className="upselling-suggestions-container">
              {groupedSuggestions.map((group, groupIndex) => {
                const groupKey = this.getSuggestionGroupKey(group[0]);
                const activeIndex = this.getCurrentGroupIndex(groupKey);
                const activeSuggestion = group[activeIndex] || group[0];
                const isSelected = activeSuggestion ? selectedSuggestions.has(activeSuggestion.id) : false;

                return (
                  <div
                    key={groupKey || groupIndex}
                    className={`upselling-suggestion-card ${isSelected ? "selected" : ""}`}
                  >
                    {group.length > 1 && (
                      <div className="upselling-variant-nav">
                        <button
                          type="button"
                          className="upselling-variant-btn"
                          onClick={() => this.changeVariant(groupKey, -1)}
                          disabled={isAdding}
                        >
                          ‹
                        </button>
                        <span className="upselling-variant-counter">
                          {activeIndex + 1}/{group.length}
                        </span>
                        <button
                          type="button"
                          className="upselling-variant-btn"
                          onClick={() => this.changeVariant(groupKey, 1)}
                          disabled={isAdding}
                        >
                          ›
                        </button>
                      </div>
                    )}

                    <div
                      className="upselling-options-stack"
                      ref={(el) => {
                        if (el) this.groupRefs[groupKey] = el;
                      }}
                    >
                      {group.map((suggestion, optionIndex) => {
                        const optionIsActive = optionIndex === activeIndex;
                        const optionIsSelected = selectedSuggestions.has(suggestion.id);
                          const imageUrl = this.getImageUrl(suggestion);

                          if (!imageUrl) {
                              return null;
                          }

                        return (
                          <div
                            key={`${groupKey || groupIndex}-${suggestion.id}`}
                            className={`upselling-option-panel ${optionIsActive ? "is-active" : ""}`}
                            aria-hidden={!optionIsActive}
                            >
                                <div className="upselling-card-image">
                                    <img
                                        src={imageUrl}
                                        alt={suggestion.name}
                                        onError={(e) => {
                                            e.target.style.display = "none";
                                        }}
                                    />
                                </div>

                                <div className="upselling-card-actions">
                                    <label className="upselling-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={optionIsSelected}
                                            onChange={() => this.handleToggleSuggestion(suggestion.id)}
                                            disabled={isAdding}
                                        />
                                        <span className="checkmark"></span>
                                    </label>

                                    <button
                                        className="upselling-quick-add-btn"
                                        onClick={() => this.handleAcceptOne(suggestion)}
                                        disabled={isAdding}
                                        title="Add this item to cart"
                                    >
                                        +
                                    </button>
                                </div>

                            <div className="upselling-card-info">
                              <h3 className="upselling-card-name">{suggestion.name}</h3>

                              {suggestion.description && suggestion.description !== "N/A" && suggestion.description !== "n/a" && (
                                <p className="upselling-card-description">
                                  {suggestion.description}
                                </p>
                              )}

                              {suggestion.aiReason && (
                                <p className="upselling-card-reason">
                                  💡 {suggestion.aiReason}
                                </p>
                              )}

                              <div className="upselling-card-footer">
                                <span className="upselling-card-price">
                                  {suggestion.price} {suggestion.currency || "€"}
                                </span>

                                {suggestion.relevanceScore && (
                                  <span className="upselling-relevance-badge">
                                    {Math.round(suggestion.relevanceScore * 100)}% <Translate value="local.match" />
                                  </span>
                                )}
                              </div>
                                </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="upselling-footer">
            <button
              className="upselling-btn upselling-btn-skip"
              onClick={this.handleSkip}
              disabled={isAdding}
            >
              <Translate value="local.next" />
            </button>

            {selectedSuggestions.size > 0 && (
              <button
                className="upselling-btn upselling-btn-primary"
                onClick={this.handleAddSelected}
                disabled={isAdding}
              >
                {isAdding ? (
                  <span>
                    <Translate value="local.adding" />...
                  </span>
                ) : (
                  <span>
                    <Translate value="local.add" /> ({selectedSuggestions.size})
                  </span>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default UpsellingSuggestions;
